import * as React from 'react';
import {
  BaSeDialogContext,
  BaSeDialogContextInterface,
  BaSeDialogModalConfig,
  RenderContentProps,
} from '../../contexts/dialog';
import { BaSeI18nContext } from '../../contexts/i18n';
import { BaSeTheme } from '../../theme';
import { BaSeButton, ButtonProps } from '../button/button/button';
import { BaSeCropper, CropperInterface } from '../cropper/cropper';
import { BaSeIcon } from '../image/icon';
import { IconButtonInputProps } from '../input/text/input';
import { BaSeFormLabel } from '../labels/form-label/form-label';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../typography/small/small1';
import { FilePickerProps, Files, Urls } from './file-picker';
import { FilePickerWrapper, HiddenInputFile } from './file-picker-styled';
import {
  ActionButtonWrapper,
  SelectedImage,
  SelectedImageProps,
} from './image-picker-styled';
import { BaSeFileInputField } from './input-field';
import { fileIsImage } from './utils';

export type Image = string | File | Blob;
export interface ImagePickerProps
  extends SelectedImageProps,
    Omit<FilePickerProps, 'pickMultipleFiles' | 'onChange'>,
    Pick<
      CropperInterface,
      | 'cropSaveButtonProps'
      | 'cropCancelButtonProps'
      | 'cropFocusColor'
      | 'cropMinWidth'
      | 'cropMinHeight'
      | 'cropMaxWidth'
      | 'cropMaxHeight'
      | 'cropAspect'
    > {
  selectedImage?: Image;
  iconNotFoundPlaceholder?: string;
  dialogContext?: React.Context<BaSeDialogContextInterface>;
  cropEnable?: boolean;
  cropModalProps?: Pick<
    BaSeDialogModalConfig,
    | 'title'
    | 'dismissible'
    | 'dismissibleIconColor'
    | 'dismissibleIconType'
    | 'dismissibleIconName'
    | 'size'
    | 'titleColor'
    | 'alignActions'
    | 'beforeClose'
    | 'afterClose'
    | 'onDismiss'
    | 'onOpen'
  >;
  updateButtonProps?: Partial<ButtonProps>;
  deleteButtonProps?: Partial<ButtonProps>;
  onFilePick?(files: Files): void;
  onUrlPick?(urls: Urls): void;
  onDelete?(deletedImage: Image): void;
}

export const BaSeImagePicker: React.FC<ImagePickerProps> = ({
  renderStyle = 'box',
  iconNotFoundPlaceholder = 'times-circle-tonescale',
  cropEnable = true,
  imageShape = 'square',
  selectedImage,
  dialogContext, // TODO encontrar melhor maneira de abrir modal?
  cropModalProps,
  cropSaveButtonProps,
  cropCancelButtonProps,
  cropFocusColor,
  cropAspect,
  cropMinWidth,
  cropMinHeight,
  cropMaxWidth,
  cropMaxHeight,
  label,
  subLabel,
  showHelpButton,
  helpButtonProps,
  pickInputProps,
  pickButtonProps,
  updateButtonProps,
  deleteButtonProps,
  placeholderImageProps,
  dragInfoText,
  dragInfoProps,
  previewImageFit = 'cover',
  onError,
  onFilePick,
  onUrlPick,
  onDelete,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [notFound, setNotFound] = React.useState(false);
  const [files, setFiles] = React.useState<Files>([]);
  const [urls, setUrls] = React.useState<Urls>([]);
  const [isDrag, setIsDrag] = React.useState(false);
  const [selectedImageSource, setSelectedImageSource] = React.useState('');

  const { showModal } = React.useContext(dialogContext ?? BaSeDialogContext);

  const { getMessage } = React.useContext(BaSeI18nContext);

  const handleChange = React.useCallback(
    (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | React.DragEvent<HTMLDivElement>,
    ) => {
      const fileList =
        (event as React.ChangeEvent<HTMLInputElement>)?.target?.files ??
        (event as React.DragEvent<HTMLDivElement>)?.dataTransfer?.files;

      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.value = '';
        }
      }, 150);

      const filteredList = Array.from(fileList).filter((f) => fileIsImage(f));

      if (!filteredList?.length) {
        return setFiles([]);
      }

      const file = filteredList[0];

      if (!cropEnable) {
        return setFiles([file]);
      }

      showModal({
        ...cropModalProps,
        content: (modalProps: RenderContentProps) => {
          const closeModalWithDelay = (message: string) =>
            setTimeout(() => modalProps?.closeModal?.(message), 50);
          return (
            <BaSeCropper
              file={file}
              circularCrop={imageShape === 'circle'}
              cropAspect={cropAspect}
              cropSaveButtonProps={cropSaveButtonProps}
              cropCancelButtonProps={cropCancelButtonProps}
              cropFocusColor={cropFocusColor}
              cropMinWidth={cropMinWidth}
              cropMinHeight={cropMinHeight}
              cropMaxWidth={cropMaxWidth}
              cropMaxHeight={cropMaxHeight}
              cancelCallback={() => closeModalWithDelay('')}
              onCropError={(message) => {
                onError?.(message);
                closeModalWithDelay(message);
              }}
              saveCallback={(croppedImage) => {
                setFiles([croppedImage as File]);
                closeModalWithDelay('');
              }}
            />
          );
        },
        title: cropModalProps?.title ?? getMessage('ImagePicker.modalTitle'),
        actions: [],
        size: cropModalProps?.size ?? 'large',
      });
    },
    [
      cropEnable,
      cropModalProps,
      imageShape,
      cropAspect,
      cropSaveButtonProps,
      cropCancelButtonProps,
      cropFocusColor,
      cropMinWidth,
      cropMinHeight,
      cropMaxWidth,
      cropMaxHeight,
      showModal,
      getMessage,
      onError,
    ],
  );

  const handleDrag = React.useCallback(
    (bool: boolean) => (event: React.DragEvent<HTMLDivElement>) => {
      event?.preventDefault();
      setIsDrag(bool);
    },
    [],
  );

  const handleDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      handleChange(event);
      setIsDrag(false);
    },
    [handleChange],
  );

  React.useEffect(() => {
    onFilePick?.(files);
    if (files) {
      setUrls(files.map((file) => URL.createObjectURL(file)));
    }
  }, [onFilePick, files]);

  React.useEffect(() => {
    onUrlPick?.(urls);
    return () => urls?.forEach((url) => URL.revokeObjectURL(url));
  }, [onUrlPick, urls]);

  React.useEffect(() => {
    if (selectedImage instanceof File || selectedImage instanceof Blob) {
      if (!fileIsImage(selectedImage)) {
        return onError?.(getMessage('filePicker.fileInvalidMessage'));
      }
      const url = URL.createObjectURL(selectedImage);
      setSelectedImageSource(url);
      return () => URL.revokeObjectURL(url);
    }
    setSelectedImageSource(selectedImage ?? '');
  }, [selectedImage, getMessage, onError]);

  React.useEffect(() => {
    if (selectedImage) {
      setNotFound(false);
    }
    if (selectedImage instanceof File) {
      return () => {
        URL.revokeObjectURL(selectedImageSource);
      };
    }
    return;
  }, [selectedImage, selectedImageSource]);

  return (
    <div className="BaSe--image-picker">
      <HiddenInputFile
        accept="image/*"
        multiple={false}
        ref={inputRef}
        onChange={handleChange}
      />
      {renderStyle === 'input-field' && (
        <>
          <BaSeFileInputField
            files={files}
            hasFiles={!!selectedImageSource}
            label={label}
            subLabel={subLabel}
            showHelpButton={showHelpButton}
            helpButtonProps={helpButtonProps}
            pickMultipleFiles={false}
            pickButtonProps={pickButtonProps as IconButtonInputProps}
            pickInputProps={pickInputProps}
            onClickToPick={() => inputRef.current?.click()}
            onClickToRemove={() => {
              onDelete?.(selectedImage as Image);
              setSelectedImageSource('');
              setFiles([]);
            }}
          />
          {selectedImageSource && (
            <BaSeSmall1
              link={{
                href: selectedImageSource,
                target: '_blank',
              }}
            >
              {getMessage('imagePicker.selectedImage')}
            </BaSeSmall1>
          )}
        </>
      )}
      {renderStyle === 'box' && (
        <>
          <BaSeFormLabel
            label={label}
            subLabel={subLabel}
            showHelpButton={showHelpButton}
            helpButtonProps={helpButtonProps}
          />
          <FilePickerWrapper
            onDragEnter={handleDrag(true)}
            onDragLeave={handleDrag(false)}
            onDragOver={handleDrag(true)}
            onDrop={handleDrop}
            isDrag={isDrag}
          >
            {selectedImage ? (
              <>
                {notFound ? (
                  <BaSeIcon
                    color={BaSeTheme.colors.institucionais.azulSebrae75}
                    size={75}
                    name={iconNotFoundPlaceholder}
                    description={getMessage('imagePicker.imageNotFound')}
                  />
                ) : (
                  <SelectedImage
                    imageShape={imageShape}
                    previewImageFit={previewImageFit}
                    src={selectedImageSource}
                    alt={getMessage('imagePicker.selectedImage')}
                    onError={() => setNotFound(true)}
                  />
                )}
                <ActionButtonWrapper>
                  <BaSeButton
                    {...updateButtonProps}
                    type={updateButtonProps?.type ?? 'primary'}
                    size={updateButtonProps?.size ?? 'small'}
                    color={updateButtonProps?.color ?? 'default'}
                    leftIcon={updateButtonProps?.leftIcon ?? 'refresh-alt'}
                    value={
                      updateButtonProps?.value ??
                      getMessage('filePicker.buttonUpdateLabel')
                    }
                    onClick={(
                      clickEvent: React.MouseEvent<HTMLButtonElement>,
                    ) => {
                      updateButtonProps?.onClick?.(clickEvent);
                      inputRef.current?.click();
                    }}
                  />
                  <BaSeButton
                    {...deleteButtonProps}
                    type={deleteButtonProps?.type ?? 'primary'}
                    size={deleteButtonProps?.size ?? 'small'}
                    color={deleteButtonProps?.color ?? 'default'}
                    leftIcon={deleteButtonProps?.leftIcon ?? 'trash-alt'}
                    value={
                      deleteButtonProps?.value ??
                      getMessage('filePicker.buttonDeleteLabel')
                    }
                    onClick={(
                      clickEvent: React.MouseEvent<HTMLButtonElement>,
                    ) => {
                      deleteButtonProps?.onClick?.(clickEvent);
                      onDelete?.(selectedImage);
                      setSelectedImageSource('');
                      setFiles([]);
                    }}
                  />
                </ActionButtonWrapper>
              </>
            ) : (
              <>
                <div style={{ pointerEvents: 'none' }}>
                  <BaSeIcon
                    color={BaSeTheme.colors.institucionais.cinzaSebrae90}
                    size={75}
                    name="image-plus"
                    description={getMessage('imagePicker.imagePlaceholder')}
                    {...placeholderImageProps}
                  />
                </div>
                <BaSeButton
                  {...(pickButtonProps as ButtonProps)}
                  type={(pickButtonProps as ButtonProps)?.type ?? 'primary'}
                  size={(pickButtonProps as ButtonProps)?.size ?? 'small'}
                  color={(pickButtonProps as ButtonProps)?.color ?? 'default'}
                  leftIcon={
                    (pickButtonProps as ButtonProps)?.leftIcon ?? 'upload'
                  }
                  value={
                    (pickButtonProps as ButtonProps)?.value ??
                    getMessage('filePicker.buttonPickLabel')
                  }
                  onClick={(
                    clickEvent: React.MouseEvent<HTMLButtonElement>,
                  ) => {
                    (pickButtonProps as ButtonProps)?.onClick?.(clickEvent);
                    inputRef.current?.click();
                  }}
                />
                <BaSeParagraph
                  {...dragInfoProps}
                  color={
                    dragInfoProps?.color ??
                    BaSeTheme.colors.institucionais.cinzaSebrae45
                  }
                >
                  {dragInfoText ?? getMessage('filePicker.image.dragInfo')}
                </BaSeParagraph>
              </>
            )}
          </FilePickerWrapper>
        </>
      )}
    </div>
  );
};

BaSeImagePicker.displayName = 'BaSeImagePicker';
